import { Component, DoCheck, ElementRef, HostListener, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { faArrowRight, faHouse, faUser, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { filter, map } from 'rxjs';
import { Payload } from 'rollbar';
import { RollbarService } from '../rollbar_config';

import { SetLocaldbTablesService } from '../app/services/service-workers/set-localdb-tables.service';
import { GlobalService } from './shared/services/global.service';
import { SecurityService } from './core/security/security.service';
import { UserJsVm } from './shared/generated/Admin/Models/User/JavaScript/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';
import { NavigationService } from './shared/navigation/navigation.service';
import { GlobalVariablesService } from './services/global-variables.service';
import { AppDatabase } from '../local-db/services/init.ldb.service';
import { InactivityTimeoutService } from './core/inactivity-timeout/inactivity-timeout.service';
import { NotificationService } from './services/notification.service';
import { CheckForUpdateService } from './services/service-workers/check-for-update.service';
import { Permission } from './core/security/generated/Permission';

/*
 * This is the application's root component.
 */
@Component({
    selector: 'pcg-root'
    , templateUrl: './app.component.html'
    , styleUrls: ['./app.component.scss']
    , providers: [ GlobalVariablesService ]
    , standalone: false
})
export class AppComponent implements OnInit, DoCheck {

    navService = inject(NavigationService);
    inactiveTimeout = inject(InactivityTimeoutService);
    public gvs = inject(GlobalVariablesService);
    public router = inject(Router);
    swUpdate = inject(SwUpdate);
    sec = inject(SecurityService);
    modalConfig = inject(NgbModalConfig);
    elRef = inject(ElementRef);
    localDb = inject(AppDatabase);
    http = inject(HttpClient);
    localDbService = inject(SetLocaldbTablesService);
    ns = inject(NotificationService);
    sw = inject(CheckForUpdateService);    

	rollbar = inject(RollbarService);
	@ViewChild('tabPanel') tabPanel: any;

	updateCheckText = '';
    mode = pcgSettings.mode;

    isLoggedIn = signal<boolean>(false); 
    isLoading = signal<boolean>(false); 

    isMobile!: boolean;
    onChangePasswordScreen!: boolean;
    precacheCached = false;
	isOnline!: boolean;

	errorCache: any = {};

	// The following are error codes thrown as a result of system security
	// and should not be logged to rollbar.
	rollbarIgnoredCodes: number[] = [
		0, // Network Error / Aborted Request
		401, // Login redirect
		403, // Access denied
		467, // 2fa not enabled
		499, // First time Inventory Setup custom redirect
		503, // Service Unavailable
		504, // Gateway timeout
	];
	
	// My Profile
	user!: UserJsVm;	

	security: Permission = localStorage.dynamicSecurity 
        ? JSON.parse(localStorage.dynamicSecurity) 
        : null;	

    // Fa icons
	faArrowRight = faArrowRight;
    faHouse = faHouse;
    faUser = faUser;
    faQuestionCircle = faQuestionCircle;
    
    @HostListener('window:resize') onResize() {
		this.isMobile = GlobalService.setIsMobile(window.innerWidth);
        this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); 
		if (this.isMobile) {
			localStorage.setItem('isNavFixed', 'false');
			this.navService.toggleNavFixed(false);
			this.navService.toggleNavOpen(false);
		}
	}

	@HostListener('window:online', ['$event']) onlineEvent(event: any) { this.uploadUsersFromLocalDb(event); }

	// Inactivity timeout needs to be injected here to have a constant timeout going in the background
	constructor() {
		this.updateCheck();
		this.configureRollbar(); // Set global rollbar settings
		this.configureModal(); // Set default modal settings
		this.setIsLoggedInHtmlClass(); // Handle is-logged-in HTML class
		// Dirty AngularJS style global router used in index.html
		(globalThis as any)['$router'] = this.router;
	}

	ngOnInit() {
        this.router.events.pipe(
            filter((e) =>
                e instanceof NavigationStart 
                || e instanceof NavigationEnd 
                || e instanceof NavigationCancel 
                || e instanceof NavigationError
            )
            , map((e) => e instanceof NavigationStart)
        ).subscribe(e => { 
            if (!e) {
                if ("serviceWorker" in navigator) {
                    navigator.serviceWorker.register(
                        'ngsw-worker.js'
                        , { updateViaCache : 'none' }
                    ).then((reg) => { reg.update(); }) // update the service worker on every page change
                }
            }
            this.isLoading.set(e); 
        });

        this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        this.isMobile = GlobalService.setIsMobile(window.innerWidth);
        // Check isNavFixed global variable on init for pages without sidenav
		if (this.gvs.isNavFixed()) {
			this.navService.isNavFixed.set(true);
			this.navService.isNavOpen.set(true);			
		}
		this.sec.user$.subscribe(user => { this.user = user; }); 

        this.badBrowserCheck(); 
        this.onChangePasswordScreen = globalThis.location.href.includes("code=fromEmail");

        // Remember if nav is fixed
		if (this.navService.isMobile()) { 
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		} else if (localStorage.getItem("isNavFixed") === "true") {
            this.navService.isNavFixed.set(true);
            this.navService.isNavOpen.set(true);
        } else {
            this.navService.isNavFixed.set(false);
            this.navService.isNavOpen.set(false);
        }

        if (globalThis.location.href.includes("code=fromEmail")) {
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		}
		this.onChangePasswordScreen =  this.gvs.onChangePasswordScreen();
	}

	ngDoCheck() { this.isLoggedIn.set(this.sec.isLoggedOn()); }

	updateCheck(): void {
        this.sw.checkForUpdates();
		this.swUpdate.versionUpdates
			.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
			.subscribe(evt => document.location.reload());
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}

    /** Sets the is-logged-in HTML class, which is used to hide the release notes links */
	setIsLoggedInHtmlClass() {
		// Add / remove is-logged-in class
		this.sec.user$.subscribe(user => {
			const isLoggedIn = !!user;
			if (isLoggedIn) { this.elRef.nativeElement.classList.add('is-logged-in'); } 
            else { this.elRef.nativeElement.classList.remove('is-logged-in'); }
		});
	}

	myBrowser() {
		if (
			(
				navigator.userAgent.includes('Opera') 
				|| navigator.userAgent.includes('OPR')
			)
		) { return 'Opera'; } 
		else if (navigator.userAgent.includes('Chrome')) { return 'Chrome'; } 
		else if (navigator.userAgent.includes('Safari')) { return 'Safari'; } 
		else if (navigator.userAgent.includes('Firefox')) { return 'Firefox'; } 
		else if (
			navigator.userAgent.includes('MSIE') 
			|| navigator.userAgent.includes('trident')
		) { return 'IE'; } 
		else { return 'unknown'; }
	}

	badBrowserCheck() {
		if (
			navigator.userAgent.includes('MSIE') 
			|| navigator.userAgent.includes('trident')
		) { this.ns.showError("Internet Explorer (IE) is an unsupported browser. Please use a supported browser.", { timeOut: 5000 }); }
	}

	/** Configure rollbar throughout the website */
	configureRollbar() {
        if (pcgSettings.mode === 'Development') { return true; }
        else {
            this.rollbar?.configure({
                // Set initial user on load (updated in subscription below)
                payload: this.getRollbarPayload(this.sec.getUser())
                , checkIgnore: (uncaught: any, args: any, item: any) => {
                    // Prevent duplicate message/URL combinations for the session
                    // Don't want to flood rollbar with errors if Angular starts looping
                    // an error message.
                    const theItem = item as any;
                    const errorKey = (theItem?.body?.message?.body ?? '') 
                        + (theItem?.body?.message?.extra?.url?.split('?')[0] ?? '') 
                        + globalThis.location.href;
                    // Do not report these errors as they are results of intented system behavior.
                    if (
                        this.errorCache[errorKey] 
                        || this.rollbarIgnoredCodes.includes(theItem?.body?.message?.extra?.status)
                    ) { return true; }

                    this.errorCache[errorKey] = true;

                    // Prevent errors in local development
                    
                    return false;
                },
            });

            // Update user if it changes
            this.sec.user$.subscribe(newUser => {
                this.rollbar?.configure({ payload: this.getRollbarPayload(newUser) });
            });
        }
	}

	/**
	 * Creates a rollbar payload with user added
	 *
	 * @param user The user to add
	 */
	getRollbarPayload(user: UserJsVm): Payload {
		return {
			environment: pcgSettings.mode
            , person: user
				? {
						id: user.id.toString()
                        , username: user.userName
                        , email: user.email
                }
				: undefined
            , client: {
				javascript: {
					source_map_enabled: true
                    , code_version: 'c0b683f'
                    , guess_uncaught_frames: true
				}
			}
		};
	}

    uploadUsersFromLocalDb(event: any) {
		// ran when user comes back online
		if (event.type == 'online') {
			this.localDb.table('EditUserTbl').toArray()
				.then(response => {
					let filteredResponse = response.filter(o => o.isUpdatedOffline);
					if (filteredResponse.length > 0) {
						// means there are entries that have been updated
						for (const element of filteredResponse) {
							this.http.post('api/Admin/Users/SaveUser', element)
								.subscribe((response: any) => {
									this.localDbService.setLocalDbTable('EditUserTbl', response.model, false);
								}
                            );
						}
					}
				}
            );
		}
	}

    navigateToDashboard(nav: any) { this.router.navigateByUrl(nav.href); }
    toReleaseNotes() { this.router.navigateByUrl("/release-notes"); }
	takeToHome() { this.router.navigateByUrl('/dashboard'); }
    takeToMyProfile() { this.router.navigateByUrl('/admin/users/edit/details/' + this.user?.id); }
}
